/**
 * Utility functions for Terms of Service agreement tracking
 * Updated to use backend API
 */

/**
 * Check if a user needs to agree to TOS (via backend API)
 * @param {Object} user - User object
 * @param {string} accessToken - Access token for backend API
 * @returns {Promise<boolean>} - true if user needs to agree to TOS
 */
export const needsTosAgreement = async (user, accessToken) => {
  if (!user || !accessToken) {
    return false // Not logged in users don't need TOS agreement
  }

  try {
    const validAgreement = await getValidTosAgreement(user, accessToken)
    return validAgreement === null // Need agreement if no valid agreement found
  } catch (error) {
    console.error('Error checking TOS agreement status:', error)
    return true // Default to requiring agreement if there's an error
  }
}

/**
 * Get the user ID for TOS tracking
 * @param {Object} user - User object
 * @returns {string} - User ID
 */
export const getUserId = (user) => {
  return user?.sub || user?.id || 'unknown'
}

/**
 * Check if TOS agreement is valid for a specific user (via backend API)
 * @param {Object} user - User object
 * @param {string} accessToken - Access token for backend API
 * @returns {Promise<Object|null>} - Agreement object if valid, null if invalid
 */
export const getValidTosAgreement = async (user, accessToken) => {
  if (!user || !accessToken) return null

  try {
    // Get the backend API base URL
    const backendBaseUrl = window.location.origin.replace('hub', 'hub-api')

    const response = await fetch(`${backendBaseUrl}/v1/tos`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      if (response.status === 404) {
        // No TOS agreement found
        return null
      }
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const tosData = await response.json()

    if (!tosData.agreed || !tosData.agreements || tosData.agreements.length === 0) {
      return null
    }

    // Return the most recent agreement
    const mostRecentAgreement = tosData.agreements.reduce((newest, current) => {
      return newest > current.time ? newest : current
    })
    return mostRecentAgreement
  } catch (error) {
    console.error('Failed to get TOS agreement from backend:', error)
    return null
  }
}

/**
 * Update TOS agreement via backend API
 * @param {string} userId - User ID (not used, extracted from token by backend)
 * @param {string} accessToken - Access token for backend API
 * @param {Object} agreementData - Agreement data (ipAddress, version)
 * @returns {Promise} - API call promise
 */
export const updateTos = async (userId, accessToken, agreementData) => {
  // console.log('TOS Update Debug Info:')
  // console.log('- User ID:', userId)
  // console.log('- Has Access Token:', !!accessToken)
  // console.log('- Agreement Data:', agreementData)

  try {
    // Get the backend API base URL
    const backendBaseUrl = window.location.origin.replace('hub', 'hub-api')

    const response = await fetch(`${backendBaseUrl}/v1/tos`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(agreementData),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    // console.log('TOS agreement successfully stored via backend:', await response.json())
    return true
  } catch (error) {
    console.error('Failed to store TOS agreement via backend:', error)
    throw error
  }
}

/**
 * Rescind TOS agreement via backend API
 * @param {string} userId - User ID (not used, extracted from token by backend)
 * @param {string} accessToken - Access token for backend API
 * @returns {Promise<boolean>} - true if successful
 */
export const rescindTos = async (userId, accessToken, agreementData) => {
  // console.log('=== Rescinding TOS Agreement ===')
  // console.log('- User ID:', userId)
  // console.log('- Has Access Token:', !!accessToken)

  try {
    // Get the backend API base URL
    const backendBaseUrl = window.location.origin.replace('hub', 'hub-api')

    const response = await fetch(`${backendBaseUrl}/v1/tos`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(agreementData),
    })

    if (!response.ok) {
      console.error('Failed to rescind TOS agreement via backend:', response)
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    // console.log('TOS agreement successfully rescinded via backend:', await response.json())
    return true
  } catch (error) {
    console.error('Failed to rescind TOS agreement via backend:', error)
    throw error
  }
}
